import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import getSpecifiedAttributes from 'utils/attributes/getSpecifiedAttributes';
import filterProducts from 'utils/attributes/filterProducts';
import getProductAttributes from 'store/product/selector/getProductAttributes';
import getProductPageSelectors from 'store/product/selector/getProductPageSelectors';
import getProductItems from 'store/product/selector/getProductItems';
import getProductId from 'store/product/selector/getProductId';
import getAvailableOptions from 'utils/attributes/getAvailableOptions';
import getProductAttributeValue from 'utils/attributes/getProductAttributeValue';
import getProductItem from 'store/product/selector/getProductItem';
import markSelectedOption from 'utils/attributes/markSelectedOption';
import {
  ALL_FORMAT_SELECTORS,
  ORIENTATION_TYPE_NAME,
  PRODUCT_TYPE_NAME,
  WALLPAPER_CUSTOM_SIZE_TYPE_NAME,
} from 'constants/app';
import ProductAttributeSelector from '../ProductAttributeSelector/ProductAttributeSelector';
import styles from './ProductAttributes.module.scss';
import SizeGuideTrigger from '../SizeGuideTrigger/SizeGuideTrigger';

interface OwnProps {
  theme?: string;
}

const ProductAttributes: FC<OwnProps> = ({ theme }) => {
  const currentProductAttributes = useSelector(getProductAttributes);
  const currentProductItem = useSelector(getProductItem);
  const productPageSelectors = useSelector(getProductPageSelectors);
  const allProducts = useSelector(getProductItems);
  const fixedSelectors = productPageSelectors
    .filter((selector) => selector.isHidden)
    .map((selector) => selector.typeName);
  const fixedAttributes = getSpecifiedAttributes(fixedSelectors, currentProductAttributes);
  const productId = useSelector(getProductId);

  if (!currentProductItem) {
    return null;
  }
  let availableProducts = filterProducts(allProducts as any[], fixedAttributes);

  const renderSelector = (selector: any) => {
    // Skip rendering wallpaper custome size selector while it's not implemented
    if (selector.typeName === WALLPAPER_CUSTOM_SIZE_TYPE_NAME) return null;

    availableProducts = filterProducts(availableProducts, fixedAttributes);
    const availableOptions = getAvailableOptions(selector, availableProducts);

    // hide product and orientation selector, if one option only
    if (availableOptions.length === 1 && [PRODUCT_TYPE_NAME, ORIENTATION_TYPE_NAME].includes(selector.typeName)) {
      return null;
    }

    // show size guide trigger
    const showSizeGuideTrigger = ALL_FORMAT_SELECTORS.includes(selector.typeName);

    const currentProductAttributeValue = getProductAttributeValue(currentProductItem.product, selector.typeName) || '';
    if (availableProducts.length === 0) {
      return null;
    }

    if (!currentProductAttributeValue) {
      return null;
    }
    const markedOptions = markSelectedOption(availableOptions, currentProductAttributeValue, productId);
    if (availableProducts.length > 0) {
      fixedAttributes.push({
        typeName: selector.typeName,
        value: currentProductAttributeValue || '',
      });
    }
    return (
      <>
        <ProductAttributeSelector
          theme={theme}
          key={`pa${selector.typeName}`}
          selector={selector}
          options={markedOptions}
        />
        {showSizeGuideTrigger && <SizeGuideTrigger />}
      </>
    );
  };

  return (
    <div className={styles.ProductAttributes}>{productPageSelectors.map((selector) => renderSelector(selector))}</div>
  );
};

export default ProductAttributes;
